// src/components/auth/SignInForm.tsx (Fixed navigation)
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Organization } from '../../types'
import { useAuthStore } from '../../stores/authStore'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'

export function SignInForm({ organization }: { organization?: Organization }) {
  const navigate = useNavigate()
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [localError, setLocalError] = useState<string>('')

  // Use the new authStore
  const {
    signIn,
    isLoading,
    isAuthenticated,
    error: authError,
    clearError,
  } = useAuthStore()

  // Navigate to dashboard when authentication succeeds
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Clear errors when component mounts or when user starts typing
  useEffect(() => {
    clearError()
  }, [clearError])

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError('')
    clearError()

    try {
      await signIn(email, password)
      // Navigation will happen automatically via useEffect above
    } catch (err) {
      // Handle any additional errors that weren't caught by the store
      setLocalError(err instanceof Error ? err.message : 'An error occurred')
    }
  }

  // Clear local error when user starts typing
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
    if (localError) setLocalError('')
    if (authError) clearError()
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    if (localError) setLocalError('')
    if (authError) clearError()
  }

  // Combine local error with auth store error
  const displayError = localError || authError

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
        <p className="text-sm text-gray-600 mt-1">
          {organization
            ? `Sign in to ${organization.name}`
            : 'Sign in to your account'}
        </p>
      </div>

      <form onSubmit={handleSignIn} className="space-y-4">
        <Input
          label="Email"
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={handleEmailChange}
          required
          disabled={isLoading}
          autoComplete="email"
        />

        <Input
          label="Password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={handlePasswordChange}
          required
          disabled={isLoading}
          autoComplete="current-password"
        />

        <div className="flex justify-end">
          <a
            href="/auth/forgot-password"
            className="text-sm text-brand-600 hover:text-brand-700 hover:underline"
          >
            Forgot your password?
          </a>
        </div>

        {displayError && (
          <div className="alert alert-error">{displayError}</div>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={isLoading}
          disabled={!email || !password}
          className="w-full"
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>
      </form>
    </div>
  )
}
